import { ApiError } from '@/api/ApiError'
import { CopyableValue } from '@/components/CopyableValue/CopyableValue'
import { SectionCard } from '@/components/SectionCard/SectionCard'
import { useOrganization } from '@/contexts/useOrganization'
import { getStripeSubscriptionLink } from '@/features/HivesOverview/components/ExternalLinks'
import { useGetAddonUsage } from '@/generated/http-clients/v3ApiComponents'
import {
  Alert,
  Box,
  Button,
  DataList,
  Grid,
  GridItem,
  Skeleton,
  Table,
  Text,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'

const NUMBER_FORMAT = new Intl.NumberFormat('en-US')

const PERIOD_DATE_FORMAT = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
})

const LAST_REPORTED_FORMAT = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  timeZone: 'UTC',
  timeZoneName: 'short',
})

const ADDON_ID = 'data_intelligence'

const STATUS_CANCELED = 'canceled'

// Display order only, not a filter — an unlisted key still renders under its raw Stripe name.
const METER_ORDER = [
  'data_intelligence_nodes',
  'data_intelligence_computations_links_nodes',
  'data_intelligence_free_allowance_nodes',
  'data_intelligence_free_allowance_computations_links_nodes',
]

const FREE_ALLOWANCE_ORDER = [
  'freeAllowanceBaseNodes',
  'freeAllowanceComputationsLinksNodes',
]

type Translate = (key: string) => unknown

type Props = {
  orgId: string
}

type ValueMap = Record<string, number>

function orderedEntries(values: ValueMap, order: string[]): [string, number][] {
  const known = order
    .filter((key) => key in values)
    .map((key): [string, number] => [key, values[key]])
  const rest = Object.entries(values)
    .filter(([key]) => !order.includes(key))
    .sort(([a], [b]) => a.localeCompare(b))
  return [...known, ...rest]
}

function labelFor(t: Translate, group: string, key: string) {
  const path = `organization.features.dataIntelligenceUsage.${group}.${key}`
  const label = String(t(path))
  return label === path ? key : label
}

function ValueList({
  t,
  group,
  entries,
}: Readonly<{ t: Translate; group: string; entries: [string, number][] }>) {
  return (
    <DataList.Root orientation="vertical" gap={4}>
      {entries.map(([key, value]) => (
        <DataList.Item key={key}>
          <DataList.ItemLabel>{labelFor(t, group, key)}</DataList.ItemLabel>
          <DataList.ItemValue>{NUMBER_FORMAT.format(value)}</DataList.ItemValue>
        </DataList.Item>
      ))}
    </DataList.Root>
  )
}

export function DataIntelligenceUsagePanel({ orgId }: Readonly<Props>) {
  const { t } = useTranslation('features', { useSuspense: false })
  const { t: tCommon } = useTranslation()
  const { orgFeatures } = useOrganization()
  const dataIntelligenceAddon = orgFeatures.data?.addons?.[ADDON_ID]
  const hasSubscriptionId = Boolean(dataIntelligenceAddon?.subscriptionId)

  // No subscriptionId means nothing to look up — skip the round trip.
  const usage = useGetAddonUsage(
    { pathParams: { orgId, addonId: ADDON_ID } },
    {
      enabled:
        Boolean(dataIntelligenceAddon) && Boolean(orgId) && hasSubscriptionId,
    },
  )

  if (!dataIntelligenceAddon) {
    return null
  }

  const handleRetry = () => {
    void usage.refetch()
  }

  const isNotFound =
    !hasSubscriptionId ||
    (usage.error instanceof ApiError && usage.error.status === 404)

  const meters = usage.data
    ? orderedEntries(usage.data.meters, METER_ORDER)
    : []
  const freeAllowances = usage.data
    ? orderedEntries(usage.data.freeAllowances, FREE_ALLOWANCE_ORDER)
    : []

  return (
    <SectionCard
      title={String(t('organization.features.dataIntelligenceUsage.title'))}
    >
      <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={6} mb={6}>
        <GridItem
          colSpan={
            usage.isSuccess && freeAllowances.length > 0
              ? undefined
              : { base: 1, md: 2 }
          }
        >
          <DataList.Root orientation="vertical" gap={3}>
            <DataList.Item>
              <DataList.ItemLabel>
                {String(
                  t(
                    'organization.features.dataIntelligenceUsage.subscription.status',
                  ),
                )}
              </DataList.ItemLabel>
              <DataList.ItemValue>
                {dataIntelligenceAddon.status}
              </DataList.ItemValue>
            </DataList.Item>
            <DataList.Item>
              <DataList.ItemLabel>
                {String(
                  t(
                    'organization.features.dataIntelligenceUsage.subscription.subscriptionId',
                  ),
                )}
              </DataList.ItemLabel>
              <DataList.ItemValue>
                {dataIntelligenceAddon.subscriptionId ? (
                  <CopyableValue
                    value={dataIntelligenceAddon.subscriptionId}
                    externalLink={getStripeSubscriptionLink(
                      dataIntelligenceAddon.subscriptionId,
                    )}
                  />
                ) : (
                  tCommon('general.notAvailable')
                )}
              </DataList.ItemValue>
            </DataList.Item>
            {usage.isSuccess && usage.data ? (
              <DataList.Item>
                <DataList.ItemLabel>
                  {String(
                    t(
                      dataIntelligenceAddon.status === STATUS_CANCELED
                        ? 'organization.features.dataIntelligenceUsage.subscription.lastPeriod'
                        : 'organization.features.dataIntelligenceUsage.subscription.currentPeriod',
                    ),
                  )}
                </DataList.ItemLabel>
                <DataList.ItemValue>
                  {`${PERIOD_DATE_FORMAT.format(
                    new Date(usage.data.periodStart),
                  )} – ${PERIOD_DATE_FORMAT.format(
                    new Date(usage.data.periodEnd),
                  )}`}
                </DataList.ItemValue>
              </DataList.Item>
            ) : null}
          </DataList.Root>
        </GridItem>

        {usage.isSuccess && freeAllowances.length > 0 ? (
          <GridItem>
            <ValueList t={t} group="freeAllowances" entries={freeAllowances} />
          </GridItem>
        ) : null}
      </Grid>

      {usage.isLoading ? <Skeleton h={10} /> : null}

      {isNotFound ? (
        <Alert.Root status="info">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>
              {String(
                t('organization.features.dataIntelligenceUsage.notFound.title'),
              )}
            </Alert.Title>
            <Alert.Description>
              {String(
                t(
                  'organization.features.dataIntelligenceUsage.notFound.description',
                ),
              )}
            </Alert.Description>
          </Alert.Content>
        </Alert.Root>
      ) : null}

      {usage.isError && !isNotFound ? (
        <Alert.Root status="warning" role="alert">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>
              {String(
                t('organization.features.dataIntelligenceUsage.error.title'),
              )}
            </Alert.Title>
            <Alert.Description>
              {String(
                t(
                  'organization.features.dataIntelligenceUsage.error.description',
                ),
              )}
            </Alert.Description>
            <Box mt={2}>
              <Button size="xs" variant="outline" onClick={handleRetry}>
                {String(
                  t('organization.features.dataIntelligenceUsage.error.retry'),
                )}
              </Button>
            </Box>
          </Alert.Content>
        </Alert.Root>
      ) : null}

      {usage.isSuccess && usage.data ? (
        <Box>
          {meters.length > 0 ? (
            <Table.Root size="sm">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>
                    {t(
                      'organization.features.dataIntelligenceUsage.columns.meterName',
                    )}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t(
                      'organization.features.dataIntelligenceUsage.columns.reportedValue',
                    )}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t(
                      'organization.features.dataIntelligenceUsage.columns.lastReported',
                    )}
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {meters.map(([key, value]) => {
                  const lastReportedAt = usage.data?.meterLastReportedAt[key]
                  return (
                    <Table.Row key={key}>
                      <Table.Cell>{labelFor(t, 'meters', key)}</Table.Cell>
                      <Table.Cell>{NUMBER_FORMAT.format(value)}</Table.Cell>
                      <Table.Cell>
                        {lastReportedAt
                          ? LAST_REPORTED_FORMAT.format(
                              new Date(lastReportedAt),
                            )
                          : tCommon('general.notAvailable')}
                      </Table.Cell>
                    </Table.Row>
                  )
                })}
              </Table.Body>
            </Table.Root>
          ) : (
            <Text>
              {String(
                t('organization.features.dataIntelligenceUsage.noMeters'),
              )}
            </Text>
          )}
        </Box>
      ) : null}
    </SectionCard>
  )
}
